import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Brain, Droplets, Check } from "lucide-react";
import { getWellnessRings } from "../lib/api";

/** Three soft Bloom-toned rings: Move (tulip), Mind (cornflower), Hydrate (aquamarine). */
function Ring({ progress = 0, size = 72, stroke = 7, color = "hsl(var(--primary))", trackColor = "hsl(var(--muted))" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(1, Math.max(0, progress)));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

export default function DailyRings({ onTapHydrate, onTapMove, onTapMind }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    getWellnessRings().then((d) => mounted && setData(d)).catch(() => {});
    return () => { mounted = false; };
  }, []);
  const items = [
    { key: "move",    icon: Activity, color: "hsl(var(--primary))",  trackColor: "hsl(var(--blush))",     onTap: onTapMove,    bg: "bg-blush" },
    { key: "mind",    icon: Brain,    color: "hsl(var(--indigo))",   trackColor: "hsl(var(--cornflower-soft))", onTap: onTapMind, bg: "bg-cornflower-soft" },
    { key: "hydrate", icon: Droplets, color: "hsl(var(--aquamarine))", trackColor: "hsl(var(--aquamarine-soft))", onTap: onTapHydrate, bg: "bg-aquamarine-soft" },
  ];
  return (
    <div className="surface-card border border-border rounded-[24px] p-4" data-testid="daily-rings-card">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-primary font-extrabold">Today's rings</p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-extrabold">Move · Mind · Hydrate</p>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2.5">
        {items.map((it) => {
          const stat = data?.[it.key] || { progress: 0, label: it.key, value: "—", complete: false };
          const Icon = it.icon;
          return (
            <button
              key={it.key}
              type="button"
              onClick={it.onTap}
              className="relative flex flex-col items-center text-center rounded-2xl p-2.5 hover:bg-muted/40 transition-colors"
              data-testid={`ring-${it.key}`}
            >
              <div className="relative">
                <Ring progress={stat.progress} color={it.color} trackColor={it.trackColor} />
                <span className="absolute inset-0 flex items-center justify-center">
                  {stat.complete ? <Check size={22} className="text-foreground" strokeWidth={2.6} /> : <Icon size={20} className="text-foreground/80" />}
                </span>
              </div>
              <p className="mt-2 text-[11.5px] font-extrabold tracking-tight">{stat.label}</p>
              <p className="text-[10px] text-muted-foreground font-medium leading-tight mt-0.5">{stat.value}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
